import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  inject,
  OnDestroy,
  signal,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormGroupDirective,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { Subject, takeUntil, combineLatest, take, Observable } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, startWith, debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  ReportingLevel,
  ReportingType,
  EligibilitySelection,
  MipsEligibilityInfo,
  ExistingPacket,
} from "@models/mips-stepper.model";
import {
  selectEligibilityError,
  selectEligibilityResult,
  selectIsCheckingEligibility,
  selectMvpRegistered,
} from "@store/selectors/mips-eligibility/mips-eligibility.selectors";
import {
  selectActivePacket,
  selectProvidersForSelectedPractice,
  selectAvailableTins,
  selectLoadingTins,
  selectOrgSubmissions,
  selectOrgSubmissionsLoading,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { MipsEligibilityActions } from "@store/actions/mips-eligibility/mips-eligibility.actions";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { selectUserAdminFilteredOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { UserAdminActions } from "@store/actions/user-admin/user-admin.actions";
import { UserService } from "@services/user.service";
import { ROLE_GROUPS } from "../../constants/roles";
import { getCurrentPerformanceYear, getAvailablePerformanceYears, isSubmissionsClosed, MIPS_START_YEAR } from "../../constants/mips-constants";
import { DashboardProviderProd } from "@models/dashboard-providers.model";
import { MipsEligibilityDto } from "@models/mips-eligibility-response";
import { OrganizationTin } from "@models/organizationTin.model";
import {
  MipsSubmissionsDialogComponent,
  SubmissionsDialogData,
} from "@components/mips-submissions-dialog/mips-submissions-dialog.component";

@Component({
  selector: "app-mips-eligibility",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: "./mips-eligibility.component.html",
  styleUrls: ["./mips-eligibility.component.css"],
})
export class MipsEligibilityComponent implements OnInit, OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actions$ = inject(Actions);

  eligibilityForm!: FormGroup;
  parentForm!: FormGroup;

  readonly startYear = MIPS_START_YEAR;
  availableYears: number[] = getAvailablePerformanceYears();
  performanceYear: number =
    this.availableYears[this.availableYears.length - 1] || this.startYear;

  @Input() initialPerformanceYear?: number;
  @Output() yearChange = new EventEmitter<number>();

  selectedReportingLevel: ReportingLevel | null = null;
  clinicianName = signal<string | null>(null);
  npiValidationError = signal<boolean>(false);
  pendingDeletePacket = signal<ExistingPacket | null>(null);
  readonly currentPerformanceYear = getCurrentPerformanceYear();

  get submissionsClosed(): boolean {
    return isSubmissionsClosed();
  }

  /** True only when submissions are closed AND the selected year is a past year (not the current active year). */
  get isSelectedYearClosed(): boolean {
    return (
      isSubmissionsClosed() &&
      this.eligibilityForm?.get('performanceYear')?.value !== getCurrentPerformanceYear()
    );
  }

  get selectedClosedYear(): number {
    return this.eligibilityForm?.get('performanceYear')?.value;
  }

  orgSearchControl = new FormControl();
  clinicianSearchControl = new FormControl<string>("");
  private isSavingPacket = false;

  filteredOrgs$!: Observable<Array<{ id: string; name: string }>>;
  filteredProviders$!: Observable<DashboardProviderProd[]>;

  activeUser = toSignal(this.store.select(selectActiveUser));
  currentPacket = toSignal(this.store.select(selectActivePacket));
  eligibilityResult = toSignal(this.store.select(selectEligibilityResult));
  mvpRegistered = toSignal(this.store.select(selectMvpRegistered));
  isLoading = toSignal(this.store.select(selectIsCheckingEligibility));
  rawErrorMessage = toSignal(this.store.select(selectEligibilityError));
  orgSubmissions = toSignal(this.store.select(selectOrgSubmissions));
  orgSubmissionsLoading = toSignal(this.store.select(selectOrgSubmissionsLoading));

  availableOrgs$ = this.store.select(selectUserAdminFilteredOrgs);
  providers$ = this.store.select(selectProvidersForSelectedPractice);
  providers = toSignal(this.providers$);
  availableTins = toSignal(this.store.select(selectAvailableTins));
  loadingTins = toSignal(this.store.select(selectLoadingTins));
  selectedOrgIndex = signal<number | null>(null);
  private tinsLoaded = signal<boolean>(false);

  get practiceTins(): OrganizationTin[] {
    return this.availableTins() || [];
  }

  get hasSingleTin(): boolean {
    return this.practiceTins.length === 1;
  }

  get hasMultipleTins(): boolean {
    return this.practiceTins.length > 1;
  }

  get hasNoTins(): boolean {
    return this.tinsLoaded() && !this.loadingTins() && this.practiceTins.length === 0;
  }

  get npiSubmissions(): any[] {
    const npi = this.eligibilityForm?.get("npi")?.value;
    if (!npi) return [];
    return (this.orgSubmissions() ?? []).filter((p: any) => p.npi === npi);
  }

  userAssignedOrgs$ = combineLatest([
    this.store.select(selectActiveUser),
    this.availableOrgs$,
    this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER),
  ]).pipe(
    map(([activeUser, allOrgs, isSuperUser]) => {
      if (!allOrgs) {
        return [];
      }

      if (isSuperUser) {
        return allOrgs;
      }

      if (!activeUser?.loggedInUser?.organizationIds) {
        return [];
      }

      return allOrgs.filter((org) =>
        activeUser.loggedInUser?.organizationIds.includes(Number(org.id)),
      );
    }),
  );

  allReportingTypes: Array<{ value: ReportingType; label: string }> = [
    { value: "traditional", label: "Traditional MIPS" },
    { value: "mvp", label: "MVP" }
  ];
//TODO add back if we add back both     { value: "both", label: "Both (Traditional + MVP)" },
  get reportingTypes(): Array<{
    value: ReportingType;
    label: string;
    disabled?: boolean;
    hint?: string;
  }> {
    const mvpRegistered = this.mvpRegistered() ?? false;
    const npiSubmissions = this.npiSubmissions;

    return this.allReportingTypes
      .map((option) => {
        const result = this.shouldShowReportingOption(
          option.value,
          mvpRegistered,
          npiSubmissions,
        );
        return {
          ...option,
          disabled: !result.show || result.disabled,
          hint: result.message,
        };
      })
      .filter((option) => !option.disabled || option.hint);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['initialPerformanceYear'];
    if (change && !change.isFirstChange()) {
      const year: number = change.currentValue;
      const currentYear = this.eligibilityForm?.get('performanceYear')?.value;
      if (year && this.eligibilityForm && year !== currentYear) {
        this.performanceYear = year;
        this.clearPacketAndResetForm();
        this.eligibilityForm.get("performanceYear")?.setValue(year, { emitEvent: false });
        const tin = this.eligibilityForm.get("tin")?.value;
        if (tin) {
          this.dispatchLoadOrgSubmissions(tin);
        }
      }
    }
  }

  ngOnInit(): void {
    this.store.dispatch(UserAdminActions.pageLoaded());

    this.filteredOrgs$ = combineLatest([
      this.userAssignedOrgs$,
      this.orgSearchControl.valueChanges.pipe(startWith("")),
    ]).pipe(
      map(([orgs, searchTerm]) => {
        const mappedOrgs = orgs.map((org) => ({
          id: org.id.toString(),
          name: org.name,
        }));

        if (!searchTerm) return mappedOrgs;

        return mappedOrgs.filter((org) =>
          org.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }),
    );

    this.filteredProviders$ = combineLatest([
      this.providers$,
      this.clinicianSearchControl.valueChanges.pipe(startWith("")),
    ]).pipe(
      map(([providers, searchTerm]) => {
        if (!providers) return [];
        if (!searchTerm) return providers;

        return providers.filter((provider) => {
          const fullName = `${provider.lastName}, ${provider.firstName} (NPI: ${provider.npi})`;
          return fullName.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }),
    );

    const defaultYear = this.initialPerformanceYear ?? this.performanceYear;
    this.eligibilityForm = this.fb.group({
      practice: ["", Validators.required],
      performanceYear: [defaultYear, Validators.required],
      tin: ["", Validators.required],
      npi: ["", Validators.required],
      reportingType: ["", Validators.required],
      reportingLevel: ["", Validators.required],
    });

    this.eligibilityForm
      .get('performanceYear')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((year) => {
        this.clearPacketAndResetForm();

        const tin = this.eligibilityForm.get("tin")?.value;
        if (tin) {
          this.dispatchLoadOrgSubmissions(tin);
        }

        this.yearChange.emit(year);
      });

    this.eligibilityForm
      .get('practice')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((practiceId) => {
        const currentPacket = this.currentPacket();
        const currentEligibility = this.eligibilityResult();

        if (currentPacket || currentEligibility) {
          this.clearPacketAndResetForm();
        }

        this.eligibilityForm.patchValue({ tin: "" }, { emitEvent: false });
        this.tinsLoaded.set(false);

        if (practiceId) {
          this.store.dispatch(
            MipsPacketActions.loadProvidersForSelectedPractice({
              orgId: Number(practiceId),
            }),
          );
          this.store.dispatch(
            MipsPacketActions.loadTinsForPractice({
              orgId: Number(practiceId),
            }),
          );
        }
      });

    this.eligibilityForm
      .get("npi")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((npi) => {
        const currentPacket = this.currentPacket();

        if (
          currentPacket?.id &&
          currentPacket.npi &&
          currentPacket.npi !== npi
        ) {
          this.clearPacketAndResetForm();

          this.eligibilityForm.patchValue(
            {
              npi: npi,
            },
            { emitEvent: false },
          );
        }

        this.updateParentFormValidity();
      });

    this.eligibilityForm
      .get('reportingType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateParentFormValidity();
      });

    this.eligibilityForm
      .get('reportingLevel')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateParentFormValidity();
      });

    this.store
      .select(selectEligibilityResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.clinicianName) {
          this.clinicianName.set(result.clinicianName);
        }
        this.updateParentFormValidity();
      });

    this.store
      .select(selectEligibilityError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        if (error) {
          this.handleError(error);
        }
      });

    combineLatest([
      this.store.select(selectAvailableTins),
      this.store.select(selectLoadingTins),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tins, loading]) => {
        if (tins && tins.length === 1 && !loading) {
          this.eligibilityForm.patchValue({ tin: tins[0].tin });
        }
        if (!loading && tins !== undefined) {
          this.tinsLoaded.set(true);
        }
        this.updateParentFormValidity();
      });

    this.eligibilityForm
      .get("tin")
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((tin) => {
        if (tin) {
          this.dispatchLoadOrgSubmissions(tin);
        }
      });

    const packet = this.currentPacket();
    if (packet && packet.id) {
      this.eligibilityForm.patchValue(
        {
          practice: packet.practice,
          performanceYear: packet.performanceYear,
          reportingType: packet.reportingType,
          npi: packet.npi,
          reportingLevel: packet.reportingLevel,
          tin: packet.tinDetails?.tin || "",
        },
        { emitEvent: false },
      );
      this.selectedReportingLevel = packet.reportingLevel as ReportingLevel;

      if (packet.practice) {
        this.store.dispatch(
          MipsPacketActions.loadProvidersForSelectedPractice({
            orgId: Number(packet.practice),
          }),
        );
        this.store.dispatch(
          MipsPacketActions.loadTinsForPractice({
            orgId: Number(packet.practice),
          }),
        );
      }

      if (packet.clinicianName) {
        this.clinicianName.set(packet.clinicianName);
      }
      const existingResult = this.eligibilityResult();

      if (!existingResult || existingResult.npi !== packet.npi) {
        const tin = this.eligibilityForm.get("tin")?.value;
        this.store.dispatch(
          MipsEligibilityActions.checkEligibility({
            performanceYear: packet.performanceYear,
            practice: packet.practice,
            npi: packet.npi,
            tin: tin || "",
          }),
        );
      }
    } else {
      const user = this.activeUser();
      if (user?.orgId) {
        // patchValue triggers the practice valueChanges subscription,
        // which dispatches loadProvidersForSelectedPractice and loadTinsForPractice
        this.eligibilityForm.patchValue({ practice: user.orgId.toString() });
      }
    }

    this.parentForm = this.ctrlContainer.form;
    if (this.parentForm) {
      this.parentForm.addControl("eligibilityInfo", this.eligibilityForm);
    }

    this.eligibilityForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateParentFormValidity();
      });

    this.updateParentFormValidity();

    this.actions$
      .pipe(ofType(MipsPacketActions.getOrCreatePacketSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        const tin = this.eligibilityForm.get("tin")?.value;
        if (tin) {
          this.dispatchLoadOrgSubmissions(tin);
        }
      });

    this.setupAutoSave();
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.removeControl("eligibilityInfo");
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAutoSave(): void {
    let lastSavedValues: any = null;

    combineLatest([
      this.eligibilityForm.valueChanges.pipe(
        startWith(this.eligibilityForm.value),
      ),
      this.store.select(selectEligibilityResult),
    ])
      .pipe(takeUntil(this.destroy$), debounceTime(500))
      .subscribe(([formValue, eligibilityResult]) => {
        const isFormValid = this.eligibilityForm.valid;
        const hasEligibilityResult = eligibilityResult !== null;
        const hasReportingLevel = this.selectedReportingLevel !== null;

        if (isFormValid && hasEligibilityResult && hasReportingLevel) {
          const currentPacket = this.currentPacket();

          if (currentPacket && currentPacket.id) {
            const hasChanges = this.hasPacketChanges(currentPacket, formValue);

            const valuesChanged =
              !lastSavedValues ||
              JSON.stringify(lastSavedValues) !== JSON.stringify(formValue);

            if (hasChanges && valuesChanged) {
              lastSavedValues = { ...formValue };
              this.savePacket();
            }
          } else {
            const valuesChanged =
              !lastSavedValues ||
              JSON.stringify(lastSavedValues) !== JSON.stringify(formValue);

            if (valuesChanged) {
              lastSavedValues = { ...formValue };
              this.savePacket();
            }
          }
        }
      });
  }

  private savePacket(): void {
    if (this.isSavingPacket) {
      return;
    }

    const formValue = this.eligibilityForm.value;
    const eligibilityResult = this.eligibilityResult();

    if (!eligibilityResult) return;

    if (isSubmissionsClosed() && formValue.performanceYear !== getCurrentPerformanceYear()) {
      return;
    }

    const alreadyExists = this.npiSubmissions.some(
      (p: any) =>
        p.reportingType === formValue.reportingType &&
        p.reportingLevel === formValue.reportingLevel,
    );
    if (alreadyExists) return;

    this.isSavingPacket = true;

    const eligibilitySelection: EligibilitySelection = {
      practice: formValue.practice,
      performanceYear: formValue.performanceYear,
      npi: formValue.npi,
      reportingType: formValue.reportingType,
      reportingLevel: formValue.reportingLevel,
      reportingMethod: this.getReportingMethod(formValue.reportingLevel),
      tin: formValue.tin,
    };

    this.store.dispatch(
      MipsPacketActions.getOrCreatePacket({
        eligibility: eligibilitySelection,
      }),
    );

    setTimeout(() => {
      this.isSavingPacket = false;
    }, 2000);
  }

  private hasPacketChanges(currentPacket: any, formValue: any): boolean {
    const practiceChanged = currentPacket.practice !== formValue.practice;
    const yearChanged =
      currentPacket.performanceYear !== formValue.performanceYear;
    const npiChanged = currentPacket.npi !== formValue.npi;
    const typeChanged = currentPacket.reportingType !== formValue.reportingType;
    const levelChanged =
      currentPacket.reportingLevel !== formValue.reportingLevel;

    return (
      practiceChanged ||
      yearChanged ||
      npiChanged ||
      typeChanged ||
      levelChanged
    );
  }

  private handleError(errorMessage: string): void {
    const errorString =
      typeof errorMessage === "string"
        ? errorMessage
        : JSON.stringify(errorMessage);

    const isNpiError =
      errorString.includes("Clinician.NotInOrganization") ||
      errorString.includes("not associated with") ||
      errorString.includes("not in organization") ||
      errorString.includes("affiliated TINs") ||
      errorString.includes("NotInOrganization");

    if (isNpiError) {
      this.npiValidationError.set(true);
    }
  }

  private clearPacketAndResetForm(): void {
    this.store.dispatch(MipsPacketActions.clearPacket());
    this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());
    this.store.dispatch(MipsPacketActions.clearMultiPacketMode());

    this.eligibilityForm.patchValue(
      {
        npi: "",
        reportingType: "",
        reportingLevel: "",
      },
      { emitEvent: false },
    );

    this.selectedReportingLevel = null;
    this.selectedOrgIndex.set(null);
    this.clinicianName.set(null);
    this.npiValidationError.set(false);

    this.clinicianSearchControl.patchValue("");

    this.updateParentFormValidity();
  }

  clearNpiError(): void {
    this.npiValidationError.set(false);
  }

  private getAvailableYears(): number[] {
    return getAvailablePerformanceYears();
  }

  getUserOrgs(): Array<{ id: string; name: string }> {
    let orgs: Array<{ id: string; name: string }> = [];

    this.userAssignedOrgs$.pipe(take(1)).subscribe((assignedOrgs) => {
      orgs = assignedOrgs.map((org) => ({
        id: org.id.toString(),
        name: org.name,
      }));
    });

    return orgs;
  }

  hasMultiplePractices(): boolean {
    return this.getUserOrgs().length > 1;
  }

  onCriteriaChange(): void {
    this.clearResults();
    this.eligibilityForm.patchValue({
      npi: "",
      reportingType: "",
      reportingLevel: "",
    });
  }

  get showMvpIdRefreshAction(): boolean {
    const packet = this.currentPacket();
    return (
      !!packet?.id && packet.reportingType === "mvp" && !packet.mvpId
    );
  }

  refreshMvpRegistration(): void {
    this.store.dispatch(MipsPacketActions.refreshMVPRegistration());
  }

  checkEligibility(): void {
    this.npiValidationError.set(false);

    if (this.hasEligibilityResult()) {
      this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());
      this.store.dispatch(MipsPacketActions.clearPacket());
      this.store.dispatch(MipsPacketActions.clearMultiPacketMode());

      this.eligibilityForm.patchValue(
        {
          reportingType: "",
          reportingLevel: "",
        },
        { emitEvent: false },
      );

      this.selectedReportingLevel = null;
      this.selectedOrgIndex.set(null);
      this.clinicianName.set(null);

      this.updateParentFormValidity();
    }

    const npiControl = this.eligibilityForm.get("npi");

    if (npiControl?.invalid) {
      npiControl.markAsTouched();
      return;
    }

    const performanceYear = this.eligibilityForm.get("performanceYear")?.value;
    const practice = this.eligibilityForm.get("practice")?.value;
    const npi = npiControl?.value;
    const tin = this.eligibilityForm.get("tin")?.value;

    this.store.dispatch(
      MipsEligibilityActions.checkEligibility({
        performanceYear,
        practice,
        npi,
        tin,
      }),
    );
  }

  shouldShowReportingOption(
    reportingType: ReportingType,
    mvpRegistered: boolean,
    existingPackets: any[],
  ): { show: boolean; disabled: boolean; message?: string } {
    const hasTraditional = existingPackets.some(
      (p: any) => p.reportingType === "traditional",
    );
    const hasMvp = existingPackets.some((p: any) => p.reportingType === "mvp");

    if (reportingType === "both") {
      const bothExist = hasTraditional && hasMvp;
      const hasOnlyOne =
        (hasTraditional && !hasMvp) || (!hasTraditional && hasMvp);

      return {
        show: true,
        disabled: false,
        message: !mvpRegistered
          ? "Not registered for MVP"
          : bothExist
            ? "You have existing Traditional and MVP submissions"
            : hasOnlyOne
              ? "Create the missing submission to complete both"
              : "Create both Traditional and MVP submissions",
      };
    }

    if (reportingType === "traditional") {
      return {
        show: true,
        disabled: false,
        message: hasTraditional
          ? "You have existing Traditional submission(s)"
          : undefined,
      };
    }

    if (reportingType === "mvp") {
      return {
        show: true,
        disabled: false,
        message: !mvpRegistered
          ? "Not registered for MVP"
          : hasMvp
            ? "You have existing MVP submission(s)"
            : undefined,
      };
    }

    return { show: false, disabled: true };
  }

  private dispatchLoadOrgSubmissions(tin: string): void {
    const orgId = this.eligibilityForm.get("practice")?.value;
    const performanceYear = this.eligibilityForm.get("performanceYear")?.value;
    if (orgId && performanceYear && tin) {
      this.store.dispatch(
        MipsPacketActions.loadOrgSubmissions({ orgId, performanceYear, tin }),
      );
    }
  }

  get hasInsufficientProvidersForGroup(): boolean {
    return (this.providers() ?? []).filter((p) => p.active === 1).length < 2;
  }

  get hasTraditionalGroupSubmission(): boolean {
    return (this.orgSubmissions() ?? []).some(
      (p: any) => p.reportingType === "traditional" && p.reportingLevel === "group",
    );
  }

  get hasMvpGroupSubmission(): boolean {
    return (this.orgSubmissions() ?? []).some(
      (p: any) => p.reportingType === "mvp" && p.reportingLevel === "group",
    );
  }

  get hasMvpSubgroupSubmission(): boolean {
    return (this.orgSubmissions() ?? []).some(
      (p: any) => p.reportingType === "mvp" && p.reportingLevel === "subgroup",
    );
  }

  get isGroupLevelBlocked(): boolean {
    if (this.hasInsufficientProvidersForGroup) return true;
    const reportingType = this.eligibilityForm.get("reportingType")?.value;
    if (reportingType === "traditional") return this.hasTraditionalGroupSubmission;
    if (reportingType === "mvp") return this.hasMvpGroupSubmission;
    if (reportingType === "both") return this.hasTraditionalGroupSubmission || this.hasMvpGroupSubmission;
    return false;
  }

  get isSubgroupLevelBlocked(): boolean {
    if (this.hasInsufficientProvidersForGroup) return true;
    return this.hasMvpSubgroupSubmission;
  }

  get groupLevelBlockedMessage(): string {
    if (this.hasInsufficientProvidersForGroup) {
      return "Group reporting requires at least 2 providers for this TIN";
    }
    const reportingType = this.eligibilityForm.get("reportingType")?.value;
    if (reportingType === "traditional") {
      return "A Traditional group submission already exists for this TIN";
    }
    if (reportingType === "mvp") {
      return "An MVP group submission already exists for this TIN";
    }
    if (reportingType === "both") {
      if (this.hasTraditionalGroupSubmission && this.hasMvpGroupSubmission) {
        return "Traditional and MVP group submissions already exist for this TIN";
      }
      if (this.hasTraditionalGroupSubmission) {
        return "A Traditional group submission already exists for this TIN";
      }
      return "An MVP group submission already exists for this TIN";
    }
    return "A group submission already exists for this TIN";
  }

  get subgroupLevelBlockedMessage(): string {
    if (this.hasInsufficientProvidersForGroup) {
      return "Subgroup reporting requires at least 2 providers for this TIN";
    }
    return "An MVP subgroup submission already exists for this TIN";
  }

  openSubmissionsDialog(): void {
    const ref = this.dialog.open<
      MipsSubmissionsDialogComponent,
      SubmissionsDialogData
    >(MipsSubmissionsDialogComponent, {
      data: { submissions: this.orgSubmissions() ?? [] },
      width: "90vw",
      maxWidth: "1100px",
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.action === "continue") {
        this.continueSubmission(result.packet);
      }
    });
  }

  continueSubmission(packet: ExistingPacket): void {
    this.eligibilityForm.patchValue(
      {
        practice: packet.practice,
        performanceYear: packet.performanceYear,
        reportingType: packet.reportingType,
        reportingLevel: packet.reportingLevel,
        npi: packet.npi,
        tin: packet.tin ?? "",
      },
      { emitEvent: false },
    );
    this.selectedReportingLevel = packet.reportingLevel as ReportingLevel;
    this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());
    this.store.dispatch(
      MipsEligibilityActions.checkEligibility({
        performanceYear: packet.performanceYear,
        practice: packet.practice,
        npi: packet.npi,
        tin: packet.tin ?? "",
      }),
    );
    this.store.dispatch(
      MipsPacketActions.loadExistingPacket({ packetId: packet.id }),
    );
  }

  confirmDelete(packet: ExistingPacket): void {
    this.pendingDeletePacket.set(packet);
  }

  cancelDelete(): void {
    this.pendingDeletePacket.set(null);
  }

  executeDelete(): void {
    const packet = this.pendingDeletePacket();
    if (!packet) return;
    this.store.dispatch(
      MipsPacketActions.deleteSubmission({
        packetId: packet.id,
        organizationId: parseInt(packet.practice, 10),
      }),
    );
    this.pendingDeletePacket.set(null);
  }

  formatReportingType(value: string): string {
    const map: Record<string, string> = {
      traditional: "Traditional MIPS",
      mvp: "MVP",
      both: "Both",
    };
    return map[value] ?? value;
  }

  formatReportingLevel(value: string): string {
    const map: Record<string, string> = {
      individual: "Individual",
      group: "Group",
      subgroup: "Subgroup",
    };
    return map[value] ?? value;
  }

  onReportingTypeSelected(reportingType: ReportingType): void {
    const existingPackets = this.npiSubmissions;

    this.selectedReportingLevel = null;
    this.eligibilityForm.patchValue({ reportingLevel: "" });

    if (reportingType === "both") {
      const traditionalPackets = existingPackets.filter(
        (p: any) => p.reportingType === "traditional",
      );
      const mvpPackets = existingPackets.filter(
        (p: any) => p.reportingType === "mvp",
      );

      const needsTraditional = traditionalPackets.length === 0;
      const needsMvp = mvpPackets.length === 0;

      if (needsTraditional && needsMvp) {
        this.eligibilityForm.patchValue({
          reportingType: "both",
        });
        this.store.dispatch(
          MipsPacketActions.setMultiPacketMode({
            mode: "both",
            currentType: "traditional",
            nextType: "mvp",
          }),
        );
      } else if (needsTraditional && mvpPackets.length > 0) {
        if (mvpPackets.length > 1) {
          this.eligibilityForm.patchValue({
            reportingType: "both",
          });
          this.store.dispatch(
            MipsPacketActions.setMultiPacketMode({
              mode: "both",
              currentType: "traditional",
              nextType: "mvp",
            }),
          );
        } else {
          const mvpPacket = mvpPackets[0];
          const mvpLevel = mvpPacket.reportingLevel as ReportingLevel;

          if (mvpLevel === "subgroup") {
            this.snackBar.open(
              "Cannot create Traditional submission: existing MVP is Subgroup level. Traditional does not support Subgroup.",
              "Close",
              { duration: 5000 },
            );
            this.eligibilityForm.patchValue({ reportingType: "" });
            return;
          }

          this.eligibilityForm.patchValue({
            reportingType: "both",
            reportingLevel: mvpLevel,
          });
          this.selectedReportingLevel = mvpLevel;

          this.store.dispatch(
            MipsPacketActions.setMultiPacketMode({
              mode: "both",
              currentType: "traditional",
              existingMvpPacketId: mvpPacket.id,
            }),
          );
        }
      } else if (needsMvp && traditionalPackets.length > 0) {
        if (traditionalPackets.length > 1) {
          this.eligibilityForm.patchValue({
            reportingType: "both",
          });
          this.store.dispatch(
            MipsPacketActions.setMultiPacketMode({
              mode: "both",
              currentType: "mvp",
              nextType: "traditional",
            }),
          );
        } else {
          const traditionalPacket = traditionalPackets[0];

          this.eligibilityForm.patchValue({
            reportingType: "both",
            reportingLevel: traditionalPacket.reportingLevel,
          });
          this.selectedReportingLevel =
            traditionalPacket.reportingLevel as ReportingLevel;

          this.store.dispatch(
            MipsPacketActions.setMultiPacketMode({
              mode: "both",
              currentType: "mvp",
              existingTraditionalPacketId: traditionalPacket.id,
            }),
          );
        }
      } else {
        if (traditionalPackets.length > 1 || mvpPackets.length > 1) {
          this.eligibilityForm.patchValue({
            reportingType: "both",
          });
          this.store.dispatch(
            MipsPacketActions.setMultiPacketMode({
              mode: "both",
              currentType: "traditional",
              nextType: "mvp",
            }),
          );
        } else {
          const traditionalPacket = traditionalPackets[0];
          const mvpPacket = mvpPackets[0];

          const traditionalLevel = traditionalPacket.reportingLevel;
          const mvpLevel = mvpPacket.reportingLevel;

          if (traditionalLevel !== mvpLevel && mvpLevel !== "subgroup") {
            this.snackBar.open(
              `Warning: Existing Traditional (${traditionalLevel}) and MVP (${mvpLevel}) submissions have different reporting levels.`,
              "Close",
              { duration: 7000 },
            );
          }

          this.eligibilityForm.patchValue({
            reportingType: "both",
            reportingLevel: traditionalLevel,
          });
          this.selectedReportingLevel = traditionalLevel as ReportingLevel;

          this.store.dispatch(
            MipsPacketActions.setMultiPacketMode({
              mode: "both",
              traditionalPacketId: traditionalPacket.id,
              mvpPacketId: mvpPacket.id,
              currentType: "traditional",
            }),
          );
        }
      }
    } else {
      this.store.dispatch(MipsPacketActions.clearMultiPacketMode());
      this.eligibilityForm.patchValue({ reportingType });
    }

    this.updateParentFormValidity();
  }

  clearResults(): void {
    this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());
    this.store.dispatch(MipsPacketActions.clearPacket());
    this.store.dispatch(MipsPacketActions.clearMultiPacketMode());

    this.clinicianName.set(null);
    this.npiValidationError.set(false);
    this.selectedOrgIndex.set(null);

    this.eligibilityForm.patchValue({
      npi: "",
      reportingType: "",
      reportingLevel: "",
    });

    this.selectedReportingLevel = null;

    this.clinicianSearchControl.patchValue("");

    this.updateParentFormValidity();
  }

  selectReportingLevel(level: ReportingLevel): void {
    if (!this.hasEligibilityResult()) {
      return;
    }

    const reportingType = this.eligibilityForm.get("reportingType")?.value;

    if (reportingType === "traditional" && level === "subgroup") {
      return;
    }

    if (reportingType === "both" && level === "subgroup") {
      return;
    }

    this.selectedReportingLevel = level;
    this.eligibilityForm.patchValue({ reportingLevel: level });

    const existingPackets = this.npiSubmissions;

    if (reportingType === "both") {
      const traditionalPacket = existingPackets.find(
        (p: any) =>
          p.reportingType === "traditional" && p.reportingLevel === level,
      );
      const mvpPacket = existingPackets.find(
        (p: any) => p.reportingType === "mvp" && p.reportingLevel === level,
      );

      if (traditionalPacket && mvpPacket) {
        this.store.dispatch(
          MipsPacketActions.setMultiPacketMode({
            mode: "both",
            traditionalPacketId: traditionalPacket.id,
            mvpPacketId: mvpPacket.id,
            currentType: "traditional",
          }),
        );
      } else if (traditionalPacket && !mvpPacket) {
        this.store.dispatch(
          MipsPacketActions.setMultiPacketMode({
            mode: "both",
            currentType: "mvp",
            existingTraditionalPacketId: traditionalPacket.id,
          }),
        );
      } else if (!traditionalPacket && mvpPacket) {
        this.store.dispatch(
          MipsPacketActions.setMultiPacketMode({
            mode: "both",
            currentType: "traditional",
            existingMvpPacketId: mvpPacket.id,
          }),
        );
      } else {
        this.store.dispatch(MipsPacketActions.clearPacket());

        this.store.dispatch(
          MipsPacketActions.setMultiPacketMode({
            mode: "both",
            currentType: "traditional",
            nextType: "mvp",
          }),
        );
      }
    } else {
      const matchingPacket = existingPackets.find(
        (p: any) =>
          p.reportingType === reportingType && p.reportingLevel === level,
      );

      if (matchingPacket && matchingPacket.id) {
        this.store.dispatch(
          MipsPacketActions.loadExistingPacket({ packetId: matchingPacket.id }),
        );
      } else {
        this.store.dispatch(MipsPacketActions.clearPacket());
      }
    }

    this.updateParentFormValidity();
  }

  isReportingLevelSelected(level: ReportingLevel): boolean {
    return this.selectedReportingLevel === level;
  }

  isReportingLevelEligible(level: ReportingLevel): boolean {
    const result = this.eligibilityResult();
    if (!result) return false;

    return true;
  }

  hasMultipleOrganizations(): boolean {
    const result = this.eligibilityResult();
    return result?.mipsEligibilityOptions
      ? result.mipsEligibilityOptions.length > 1
      : false;
  }

  needsOrgSelection(): boolean {
    const result = this.eligibilityResult();
    if (!result?.mipsEligibilityOptions) return false;

    return this.hasMultipleOrganizations() && !result.npi;
  }

  selectOrganization(org: MipsEligibilityDto, index: number): void {
    this.selectedOrgIndex.set(index);
    this.store.dispatch(
      MipsEligibilityActions.selectOrganization({ organization: org }),
    );
  }

  shouldShowReportingLevel(level: ReportingLevel): boolean {
    const reportingType = this.eligibilityForm.get("reportingType")?.value;

    if (!reportingType) {
      return false;
    }

    if (reportingType === "traditional") {
      return level !== "subgroup";
    }

    if (reportingType === "mvp") {
      return true;
    }

    if (reportingType === "both") {
      return level !== "subgroup";
    }

    return true;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.eligibilityForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  isIndividualEligible(): boolean {
    return this.eligibilityResult()?.individualEligible ?? false;
  }

  isGroupEligible(): boolean {
    return this.eligibilityResult()?.groupEligible ?? false;
  }

  isSubgroupEligible(): boolean {
    return this.eligibilityResult()?.subgroupEligible ?? false;
  }

  hasEligibilityResult(): boolean {
    return this.eligibilityResult() !== null;
  }

  hasAnyEligibility(): boolean {
    const result = this.eligibilityResult();
    return (
      result?.individualEligible ||
      result?.groupEligible ||
      result?.subgroupEligible ||
      result?.individualOptInEligible ||
      result?.groupOptInEligible ||
      result?.subgroupOptInEligible ||
      false
    );
  }

  isNpiValid(): boolean {
    const npiControl = this.eligibilityForm.get("npi");
    return !!(npiControl && npiControl.valid);
  }

  hasReportingTypeSelected(): boolean {
    const reportingTypeControl = this.eligibilityForm.get("reportingType");
    return !!(reportingTypeControl && reportingTypeControl.value);
  }

  getEligibilitySelection(): EligibilitySelection | null {
    if (!this.eligibilityForm.valid) {
      return null;
    }

    const formValue = this.eligibilityForm.value;

    return {
      npi: formValue.npi,
      practice: formValue.practice,
      performanceYear: formValue.performanceYear,
      reportingLevel: formValue.reportingLevel,
      reportingType: formValue.reportingType,
      reportingMethod: this.getReportingMethod(formValue.reportingLevel),
      tin: formValue.tin,
    };
  }

  private getReportingMethod(reportingLevel: ReportingLevel): string {
    const eligibilityType = this.getEligibilityType(reportingLevel);

    switch (eligibilityType) {
      case "required":
        return "required";
      case "opt-in":
        return "opt-in";
      case "not-eligible":
        return "voluntary";
      default:
        return "voluntary";
    }
  }

  private updateParentFormValidity(): void {
    if (this.parentForm) {
      const isValid =
        this.eligibilityForm.valid &&
        this.hasEligibilityResult() &&
        this.hasReportingTypeSelected() &&
        this.selectedReportingLevel !== null &&
        !this.hasNoTins;

      if (isValid) {
        this.parentForm.setErrors(null);
      } else {
        this.parentForm.setErrors({ incomplete: true });
      }
      this.cdr.markForCheck();
    }
  }

  hasAnyOptInEligibility(): boolean {
    const result = this.eligibilityResult();
    return (
      result?.individualOptInEligible ||
      result?.groupOptInEligible ||
      result?.subgroupOptInEligible ||
      false
    );
  }

  isIndividualOptInEligible(): boolean {
    return this.eligibilityResult()?.individualOptInEligible ?? false;
  }

  isGroupOptInEligible(): boolean {
    return this.eligibilityResult()?.groupOptInEligible ?? false;
  }

  isSubgroupOptInEligible(): boolean {
    return this.eligibilityResult()?.subgroupOptInEligible ?? false;
  }

  getEligibilityType(
    level: ReportingLevel,
  ): "required" | "opt-in" | "not-eligible" {
    const result = this.eligibilityResult();
    if (!result) return "not-eligible";

    switch (level) {
      case "individual":
        if (result.individualEligible) return "required";
        if (result.individualOptInEligible) return "opt-in";
        return "not-eligible";
      case "group":
        if (result.groupEligible) return "required";
        if (result.groupOptInEligible) return "opt-in";
        return "not-eligible";
      case "subgroup":
        if (result.subgroupEligible) return "required";
        if (result.subgroupOptInEligible) return "opt-in";
        return "not-eligible";
      default:
        return "not-eligible";
    }
  }
}
